import { HtmlHTMLAttributes } from 'react';
import { styled } from 'themes';

import { ColorVariantType, IconType } from 'types';

interface IconProps extends HtmlHTMLAttributes<HTMLElement> {
  /**
   * Tamanho do icone
   * @default 'md'
   */
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | '1x'
    | '2x'
    | '3x'
    | '4x'
    | '5x'
    | '6x'
    | '7x'
    | '8x'
    | '9x'
    | '10x';
  /**
   * Cor do icone
   * @default 'inherit'
   */
  color?: ColorVariantType;
  /**
   * Caso `true` deixa o elemento com 100% na largura
   */
  fixedWidth?: boolean;
  /**
   * Inverte o icone
   * @default false
   */
  inverse?: boolean;
  /**
   * Caso `true` adiciona uma animação de spinner para o icone
   * @default false
   */
  spin?: boolean;
  /**
   * Adiciona uma borda ao icone
   * @default false
   */
  border?: boolean;
  /**
   * Caso `true` faz uma animação de pulse
   * @default false
   */
  pulse?: boolean;
  /**
   * Puxa o icone para a direita ou para o esquerda
   */
  pull?: 'right' | 'left';
  /**
   * Rotaciona o icone em graus
   * @default 0
   */
  rotation?: 0 | 90 | 180 | 270;
  /**
   * Vira o icone
   */
  flip?: 'horizontal' | 'vertical' | 'both';
  /**
   * Nome do icone e variante
   */
  icon?: string | string[] | object;
  /**
   * Nome da class do elemento
   */
  className?: string;
}

const normalizeIcon = (icon: any) => {
  let classes: IconType = { prefix: '', iconName: '' };

  if (typeof icon === 'object' && icon.prefix && icon.iconName) {
    classes = icon;
  }

  if (Array.isArray(icon) && icon.length === 2) {
    classes = { prefix: icon[0], iconName: icon[1] };
  }

  if (typeof icon === 'string') {
    classes = { prefix: 'fal', iconName: icon };
  }

  return `${classes.prefix} fa-${classes.iconName}`;
};

const normalizeClassName = (className: string) =>
  className ? className.split(' ') : [];

const classList = (properties: any) => {
  const classes = {
    'fa-spin': properties.spin,
    'fa-inverse': properties.inverse,
    'fa-fw': properties.fixedWidth,
    'fa-border': properties.border,
    'fa-pulse': properties.pulse,
    [`fa-flip-${properties.flip}`]: properties.flip,
    [`fa-rotate-${properties.rotation}`]: properties.rotation,
    [`fa-pull-${properties.pull}`]: properties.pull,
    [`fa-${properties.size}`]: properties.size,
  };

  return Object.keys(classes)
    .map((key) => (classes[key] ? key : null))
    .filter((key) => key);
};

const FontIcon = styled('i', {
  verticalAlign: 'baseline',
});

const Icon = ({
  size = 'md',
  fixedWidth = false,
  inverse = false,
  spin = false,
  border = false,
  pulse = false,
  pull,
  rotation,
  flip,
  icon,
  className = '',
  ...rest
}: IconProps) => {
  const mergeProps = {
    ...rest,
    size,
    fixedWidth,
    inverse,
    spin,
    border,
    pulse,
    pull,
    rotation,
    flip,
  };

  return (
    <FontIcon
      className={[
        normalizeIcon(icon),
        ...classList(mergeProps),
        ...normalizeClassName(className),
      ].join(' ')}
      aria-hidden="true"
    />
  );
};

export default Icon;
